import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { DefaultPostResponse } from './../models/api.model';
import { ToastService } from './toast.service';
import { SESSION_ID } from '../constants/authentication.constants';
import { WindowService } from './window.service';

interface CustomOptions {
  showToast?: boolean;
  sendAsMultiPart?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  constructor(
    private http: HttpClient,
    public router: Router,
    private toastService: ToastService,
    private windowService: WindowService
  ) {}

  public get<T>(url: string, options?: Parameters<typeof this.http.get>[1] & CustomOptions) {
    if (options) {
      options.observe = 'response' as any;
    } else {
      options = { observe: 'response' as any };
    }

    return this.http.get<any>(`${environment.endpoints.apiUrl}/${url}`, options).pipe(
      map((res) => {
        return res.body as T;
      }),
      catchError((e) => this.handleError(e, options?.showToast))
    );
  }

  /**
   * Sends data to the server using the navigator.sendBeacon() method.
   * This method is designed to be called when the application is being unloaded.
   * @param {string} url - The API endpoint to send data to.
   * @param {any} data - The data to send to the server.
   * @returns {boolean} - Returns true if the beacon was successfully queued, otherwise false.
   */
  public sendBeacon(url: string, data: any) {
    const apiUrl = `${environment.endpoints.apiUrl}/${url}`;
    console.log(`sending beacon with this body: ${JSON.stringify(data)}`);
    fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        SessionId: this.windowService.getSessionIdFromWindow() ?? '',
      },
      body: JSON.stringify(data),
      keepalive: true,
    });
  }

  public objectToQueryString(obj: any): string {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(obj)) {
      params.append(key, value as string);
    }
    return params.toString();
  }

  public post<T = DefaultPostResponse>(
    url: string,
    data: any = {},
    options?: Parameters<typeof this.http.post>[2] & CustomOptions
  ) {
    // transform data input to formData
    if (options?.sendAsMultiPart) {
      let formData = new FormData();
      for (const [key, value] of Object.entries<any>(data)) {
        formData.set(key, value);
      }
      data = formData;
    }

    if (options) {
      options.observe = 'response' as any;
    } else {
      options = { observe: 'response' as any };
    }
    console.log(`${environment.endpoints.apiUrl}/${url}`);
    return this.http.post<any>(`${environment.endpoints.apiUrl}/${url}`, data, options).pipe(
      map((res) => {
        return res.body as T;
      }),
      catchError((e, caught) => {
        return this.handleError(e, options?.showToast);
      })
    );
  }

  private handleError = (error: HttpErrorResponse, showToast = true) => {
    let message = '';
    if (error.error instanceof ErrorEvent) {
      // client-side error
      message = error.error.message;
    } else {
      // server-side error
      message = error.error; //message;
    }

    message = message || `Something went wrong, please try again later`;

    if (showToast) this.toastService.show(message, { classname: 'bg-danger text-light', autohide: false });

    return throwError(() => message);
  };
}
